/**
 * AI Context Provider
 * 
 * Global state management for AI features including capabilities,
 * alerts, suggestions, and active operations.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { aiApi, AICapability, ProactiveAlert, SmartSuggestion } from '../domains/ai/api';

interface AIContextValue {
  // Capabilities
  capabilities: AICapability[];
  isLoadingCapabilities: boolean;
  reloadCapabilities: () => Promise<void>;

  // Proactive Alerts
  alerts: ProactiveAlert[];
  unreadAlertCount: number;
  isLoadingAlerts: boolean;
  dismissAlert: (alertId: string) => Promise<void>;
  reloadAlerts: () => Promise<void>;

  // Suggestions
  suggestions: SmartSuggestion[];
  isLoadingSuggestions: boolean;
  reloadSuggestions: () => Promise<void>;

  // UI State
  isCommandPaletteOpen: boolean;
  setIsCommandPaletteOpen: (open: boolean) => void;

  // Active Operations
  activeOperations: Map<string, any>;
  addOperation: (id: string, operation: any) => void;
  removeOperation: (id: string) => void;
}

const AIContext = createContext<AIContextValue | undefined>(undefined);

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within AIProvider');
  }
  return context;
};

interface AIProviderProps {
  children: ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // Capabilities
  const [capabilities, setCapabilities] = useState<AICapability[]>([]);
  const [isLoadingCapabilities, setIsLoadingCapabilities] = useState(false);

  // Alerts
  const [alerts, setAlerts] = useState<ProactiveAlert[]>([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);

  // Suggestions
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // UI State
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  // Active Operations
  const [activeOperations, setActiveOperations] = useState<Map<string, any>>(new Map());

  // Load capabilities on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      reloadCapabilities();
    }
  }, [isAuthenticated, user]);

  // Load alerts periodically
  useEffect(() => {
    if (isAuthenticated && user) {
      reloadAlerts();
      
      // Refresh alerts every 5 minutes
      const interval = setInterval(reloadAlerts, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isAuthenticated, user]);

  const reloadCapabilities = async () => {
    if (!user) return;
    
    setIsLoadingCapabilities(true);
    try {
      const caps = await aiApi.getAvailableCapabilities();
      setCapabilities(caps);
    } catch (error) {
      console.error('Failed to load AI capabilities:', error);
    } finally {
      setIsLoadingCapabilities(false);
    }
  };

  const reloadAlerts = async () => {
    if (!user) return;

    setIsLoadingAlerts(true);
    try {
      // Temporarily disabled due to 500 errors
      // const response = await aiApi.getProactiveAlerts();
      // setAlerts(response.alerts || []);
      // setUnreadAlertCount(response.unreadCount || 0);
      setAlerts([]);
      setUnreadAlertCount(0);
    } catch (error) {
      console.error('Failed to load proactive alerts:', error);
      // Don't show error to user - alerts are non-critical
    } finally {
      setIsLoadingAlerts(false);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      await aiApi.dismissProactiveAlert(alertId);
      setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      setUnreadAlertCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
      throw error;
    }
  };

  const reloadSuggestions = async () => {
    if (!user) return;

    setIsLoadingSuggestions(true);
    try {
      const sugs = await aiApi.getSuggestions();
      setSuggestions(sugs);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const addOperation = (id: string, operation: any) => {
    setActiveOperations((prev) => {
      const next = new Map(prev);
      next.set(id, operation);
      return next;
    });
  };

  const removeOperation = (id: string) => {
    setActiveOperations((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const value: AIContextValue = {
    capabilities,
    isLoadingCapabilities,
    reloadCapabilities,
    
    alerts,
    unreadAlertCount,
    isLoadingAlerts,
    dismissAlert,
    reloadAlerts,
    
    suggestions,
    isLoadingSuggestions,
    reloadSuggestions,
    
    isCommandPaletteOpen,
    setIsCommandPaletteOpen,
    
    activeOperations,
    addOperation,
    removeOperation,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};
